import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type { BuilderCriterionRequest, SaveLibraryCriterionResult, SavedCriterionView } from '@nest-recruit/shared';
import { apiDelete, apiGet, apiPost, type ApiError } from '../../../lib/api-client';

/** F5.25 — the criteria library (ADR 0048): saved by hand, copied into a job, never linked. */

export const CRITERIA_LIBRARY_KEY = ['criteria-library'] as const;

export function useCriteriaLibrary(isEnabled: boolean): UseQueryResult<readonly SavedCriterionView[], ApiError> {
  return useQuery({
    queryKey: CRITERIA_LIBRARY_KEY,
    queryFn: () => apiGet<readonly SavedCriterionView[]>('/criteria-library'),
    placeholderData: (previous) => previous,
    enabled: isEnabled,
  });
}

export function useSaveToLibrary() {
  const queryClient = useQueryClient();
  return useMutation<SaveLibraryCriterionResult, ApiError, BuilderCriterionRequest>({
    mutationFn: (criterion) => apiPost<SaveLibraryCriterionResult>('/criteria-library', { criterion }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: CRITERIA_LIBRARY_KEY }),
  });
}

/** Gone from the list at once (CLAUDE.md §5a rule 1); a refusal puts it back. */
export function useRemoveFromLibrary() {
  const queryClient = useQueryClient();
  return useMutation<unknown, ApiError, SavedCriterionView, { readonly before: readonly SavedCriterionView[] | undefined }>({
    mutationFn: (saved) => apiDelete(`/criteria-library/${saved.id}`, { version: saved.version }),
    onMutate: async (saved) => {
      await queryClient.cancelQueries({ queryKey: CRITERIA_LIBRARY_KEY });
      const before = queryClient.getQueryData<readonly SavedCriterionView[]>(CRITERIA_LIBRARY_KEY);
      if (before) queryClient.setQueryData(CRITERIA_LIBRARY_KEY, before.filter((item) => item.id !== saved.id));
      return { before };
    },
    onError: (_error, _saved, context) => {
      if (context?.before) queryClient.setQueryData(CRITERIA_LIBRARY_KEY, context.before);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: CRITERIA_LIBRARY_KEY }),
  });
}
